// src/services/PasswordResetGuard.ts
/**
 * A recovery link signs the user in *before* they have chosen a new password —
 * that is how token-based recovery works everywhere, and it is not the problem.
 * The problem is what the session can do in the meantime: without this flag,
 * clicking the link in the email and then closing the tab left a full, working
 * session behind. The mail became a login link for whoever could read it, even
 * when the password was never changed.
 *
 * So the arrival is marked here, and the flag outlives a reload or a closed tab
 * on purpose: coming back later must land on the reset page again, not on the
 * dashboard. It is cleared only by actually setting a new password, or by
 * leaving deliberately — and leaving signs the session out.
 *
 * `localStorage` rather than `sessionStorage` for exactly that reason: a
 * session-scoped flag would evaporate on the close and hand back the session
 * this exists to withhold.
 */

const KEY = 'ts_password_reset_pending';

/**
 * Fired on mark and on clear. `App` decides which whole tree to render from
 * this flag, and a plain `localStorage` write does not re-render anything —
 * without the event the user would set a new password and stay staring at the
 * reset page until a manual reload.
 */
export const PASSWORD_RESET_PENDING_EVENT = 'tradingspace:password-reset-pending';

const announce = () => {
  try {
    window.dispatchEvent(new Event(PASSWORD_RESET_PENDING_EVENT));
  } catch {
    /* no window — nothing is listening either */
  }
};

export const PasswordResetGuard = {
  /** Called when a recovery callback establishes the session. */
  mark(): void {
    try {
      window.localStorage.setItem(KEY, '1');
    } catch {
      /* private mode / storage disabled — the gate degrades to off */
    }
    announce();
  },

  isPending(): boolean {
    try {
      return window.localStorage.getItem(KEY) === '1';
    } catch {
      return false;
    }
  },

  clear(): void {
    try {
      window.localStorage.removeItem(KEY);
    } catch {
      /* nothing to clear */
    }
    announce();
  },
};

export default PasswordResetGuard;
